/**
 * Modal overlay shown when the user's session is about to expire due to
 * inactivity (STIG V-222389/V-222390). Displays a countdown and a
 * "Continue Session" button that resets the server-side idle timer.
 */

interface IdleWarningModalProps {
  secondsLeft: number
  onContinue: () => void
}

export function IdleWarningModal({ secondsLeft, onContinue }: IdleWarningModalProps) {
  const minutes = Math.floor(secondsLeft / 60)
  const seconds = secondsLeft % 60
  const timeDisplay =
    minutes > 0 ? `${minutes}m ${seconds.toString().padStart(2, '0')}s` : `${seconds}s`

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 max-w-md rounded-lg border border-warning/50 bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/20 text-warning">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-content">Session Expiring</h2>
        </div>

        <p className="mb-2 text-sm text-content/80">
          Your session will expire in{' '}
          <span className="font-mono font-bold text-warning">{timeDisplay}</span> due to inactivity.
        </p>
        <p className="mb-6 text-xs text-content/60">
          Click below to continue working, or you will be logged out automatically.
        </p>

        <button
          onClick={onContinue}
          className="w-full rounded-md bg-interactive px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface"
        >
          Continue Session
        </button>
      </div>
    </div>
  )
}
